import { useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Input,
  Text,
  Tooltip,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Avatar,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Spinner,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import axios from "../../axiosConfig";
import ChatLoading from "../ChatLoading";
import ProfileModal from "./ProfileModal";
import { ChatState } from "../../Context/ChatProvider";
import _ from "lodash";
import logo from "../../assets/Logo.png";
// import { getSender } from "../../config/ChatLogics";

const SideDrawer = () => {
  const [search, setSearch] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);

  const {
    setSelectedChat,
    user,
    chats,
    setChats,

    setUnreadCounts,
  } = ChatState();

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const logoutHandler = () => {
    localStorage.removeItem("userInfo");
    if ("caches" in window) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((key) => caches.delete(key))));
    }
    sessionStorage.clear();
    navigate("/");
    window.location.reload();
  };

  const handleSearch = useCallback(
    async (query) => {
      if (!query.trim()) {
        toast({
          title: "Search field is empty",
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "top-left",
        });
        return;
      }

      try {
        setLoading(true);
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
        const { data } = await axios.get(`/api/user?search=${query}`, config);
        setSearchResult(data);
      } catch (error) {
        toast({
          title: "Error occurred",
          description: "Unable to fetch search results",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-left",
        });
      } finally {
        setLoading(false);
      }
    },
    [toast, user.token]
  );

  const debounceSearch = useMemo(
    () => _.debounce(handleSearch, 300),
    [handleSearch]
  );

  const handleInputChange = (e) => {
    setSearch(e.target.value);
    debounceSearch(e.target.value);
  };

  const accessChat = async (userId) => {
    try {
      setLoadingChat(true);
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      };
      const { data } = await axios.post(`/api/chat`, { userId }, config);
      if (!chats.find((c) => c._id === data._id)) {
        setChats([data, ...chats]);
      }
      setSelectedChat(data);
      setUnreadCounts((prev) => ({ ...prev, [data._id]: 0 })); // Reset unread count
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    } finally {
      setLoadingChat(false);
    }
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bg="white"
        w="100%"
        p="5px 10px"
        borderWidth="5px"
      >
        <Tooltip label="Search Users to chat" hasArrow placement="bottom-end">
          <Button variant="ghost" onClick={onOpen}>
            <i className="fas fa-search" />
            <Text display={{ base: "none", md: "flex" }} px={4}>
              Search User
            </Text>
          </Button>
        </Tooltip>

        <Box display="flex" alignItems="center">
          <img
            src={logo}
            alt="BB8"
            style={{
              width: "100%",
              height: "30px",
              marginRight: "10px",
              cursor: "pointer",
            }}
          />
        </Box>

        <Box display="flex" alignItems="center">
          <Menu>
            <MenuButton as={Button} bg="white" rightIcon={<ChevronDownIcon />}>
              <Avatar
                size="sm"
                cursor="pointer"
                name={user.name}
                src={user.pic}
              />
            </MenuButton>
            <MenuList>
              <ProfileModal user={user}>
                <MenuItem>My Profile</MenuItem>
              </ProfileModal>
              <MenuDivider />
              <MenuItem onClick={logoutHandler}>Logout</MenuItem>
            </MenuList>
          </Menu>
        </Box>
      </Box>

      <Drawer placement="left" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader borderBottomWidth="1px">Search Users</DrawerHeader>
          <DrawerBody>
            <Box display="flex" pb={2}>
              <Input
                placeholder="Search by name or email"
                mr={2}
                value={search}
                onChange={handleInputChange}
              />
            </Box>
            {loading ? (
              <ChatLoading />
            ) : (
              <Box>
                {searchResult.map((u) => (
                  <Box
                    key={u._id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p="10px"
                    borderBottom="1px solid #e2e8f0"
                    cursor="pointer"
                    onClick={() => accessChat(u._id)}
                    _hover={{ bg: "gray.100" }}
                  >
                    <Box>
                      <Text fontWeight="bold">{u.name}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {u.email}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            {loadingChat && <Spinner ml="auto" display="flex" />}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Chat List with unread badge
      <Box mt={3} p={3}>
        {chats.map((chat) => {
          const otherUser = chat.isGroupChat
            ? null
            : chat.users.find((u) => u._id !== user._id);
          const unreadCount = unreadCounts[chat._id] || 0;

          return (
            <Box
              key={chat._id}
              onClick={() => {
                setSelectedChat(chat);
                setUnreadCounts((prev) => ({ ...prev, [chat._id]: 0 }));
              }}
              cursor="pointer"
              _hover={{ background: "#f1f1f1" }}
              p={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderBottom="1px solid #eee"
            >
              <Box>
                <Text fontWeight="bold">
                  {chat.isGroupChat
                    ? chat.chatName
                    : getSender(user, chat.users)}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {chat.latestMessage?.sender?.name}:{" "}
                  {chat.latestMessage?.content}
                </Text>
              </Box>
              {unreadCount > 0 && (
                <Badge colorScheme="red" borderRadius="full" px={2}>
                  {unreadCount}
                </Badge>
              )}
            </Box>
          );
        })}
      </Box> */}
    </>
  );
};

export default SideDrawer;
